import { PRODUCT_BULK_ELIGIBILITY_OUTCOMES } from "@price-rescue/domain/product-bulk-actions";
import { defineStore } from "pinia";
import { MerchantApiError } from "../authentication/errors.js";
import { freezeProductBulkSelection } from "./product-bulk-selection.js";
import type { EligibilityResetResult, EligibilityResetService } from "./eligibility-reset.js";

export const useEligibilityResetStore = defineStore("product-eligibility-reset", {
  state: (): {
    service?: EligibilityResetService;
    selection: string[];
    preview?: EligibilityResetResult;
    result?: EligibilityResetResult;
    error?: string;
    submitting: boolean;
    needsRefresh: boolean;
  } => ({ selection: [], submitting: false, needsRefresh: false }),
  actions: {
    configure(service: EligibilityResetService): void {
      this.service = service;
    },
    clear(): void {
      if (this.submitting || this.needsRefresh) return;
      this.selection = [];
      delete this.preview;
      delete this.result;
      delete this.error;
    },
    async prepare(productIds: string[]): Promise<void> {
      if (this.submitting || this.needsRefresh) return;
      this.clear();
      this.submitting = true;
      try {
        this.selection = freezeProductBulkSelection(productIds);
        if (!this.service) throw new Error("Eligibility reset is unavailable.");
        this.preview = await this.service.preview([...this.selection]);
      } catch (error: unknown) {
        this.error = error instanceof Error ? error.message : "Could not preview eligibility.";
      } finally {
        this.submitting = false;
      }
    },
    async execute(refresh: () => Promise<void>): Promise<void> {
      if (this.submitting || this.needsRefresh || !this.preview || !this.service) return;
      this.submitting = true;
      delete this.error;
      try {
        this.result = await this.service.execute([...this.selection]);
        this.needsRefresh = true;
        delete this.preview;
        await this.refreshResults(refresh);
      } catch (error: unknown) {
        this.error =
          error instanceof Error ? error.message : "Eligibility reset could not complete.";
        if (
          !(error instanceof MerchantApiError) ||
          ["network", "server", "unknown"].includes(error.kind)
        ) {
          this.result = {
            previews: [],
            updated: [],
            failed: [],
            skipped: [],
            unconfirmed: this.selection.map((productId) => ({
              productId,
              code: PRODUCT_BULK_ELIGIBILITY_OUTCOMES.UNCONFIRMED,
              message: "The outcome is unknown. Refresh and review before retrying.",
            })),
          };
          this.needsRefresh = true;
          delete this.preview;
        }
      } finally {
        this.submitting = false;
      }
    },
    async refresh(refresh: () => Promise<void>): Promise<void> {
      if (this.submitting) return;
      this.submitting = true;
      try {
        await this.refreshResults(refresh);
      } finally {
        this.submitting = false;
      }
    },
    async refreshResults(refresh: () => Promise<void>): Promise<void> {
      try {
        await refresh();
        this.needsRefresh = false;
        delete this.error;
      } catch {
        this.needsRefresh = true;
        this.error = "Refresh products and review the results before another batch.";
      }
    },
  },
});
